/*
 * Runs callback when the document is fully loaded (may be now or later).
 * Just like jQuery.ready.
 */
export function documentReady(callback: () => void) {
    // @ts-ignore
    if (document.readyState != 'loading') {
        callback();
    } else {
        document.addEventListener('DOMContentLoaded', callback);
    }
}

/*
 * Navigation hack, borrowed from bootstrap
 * key is used to check what "key" has the current control
 * Only the last controller can remove the navOpen
 */
export function navFullscreen(key: string, force?: boolean): void {
    // Throttle this to prevent multiple calls overriding each other!
    const $body = document.body;
    const controller = $body.dataset.navOpen;
    const state = controller == key;

    if (force != null && force == state)
        return;

    if (state) {
        delete $body.dataset.navOpen;
    } else {
        $body.dataset.navOpen = key;
    }
}

/*
 * Get all the children elements, with an optional selector
 * Just like jQuery.children.
 */
export function children<T extends Element>(element: Element, selector?: string): T[] {
    const $children = Array.from(element.children) as T[];

    if (selector?.length)
        return $children.filter($child => $child.matches(selector));

    return $children;
}

/*
 * Get all the sibling elements, with an optional selector
 * Just like jQuery.siblings.
 */
export function siblings<T extends Element>(element: Element, selector?: string): T[] {
    if (element.parentElement == null)
        return [];

    return children<T>(element.parentElement, selector)
        .filter($sibling => $sibling !== element);
}

/*
 * Get all the ancestor elements, with an optional selector
 * Just like jQuery.siblings.
 */
export function ancestors<T extends Element>(element: Element, selector?: string): T[] {
    const $ancestors: T[] = [];
    let node = element;

    while (node.parentElement != null) {
        node = node.parentElement;

        $ancestors.push(node as T);
    }

    if (selector?.length)
        return $ancestors.filter($ancestor => $ancestor.matches(selector));

    return $ancestors;
}

/*
 * Set attribute but compatible with boolean and number values
 */
export function setAttribute(element: Element, name: string, value: string | boolean | number): void {
    element.setAttribute(name, String(value));
}

/*
 * Finds the element with the specified ID, but with generic for Element type
 */
export function findElementById<T extends Element = HTMLElement>(id: string | null): T | null {
    if (!id?.length) {
        return null;
    }

    return document.getElementById(id) as T | null;
}

/*
 * Gets the nearest ancestor or self which matches the selector.
 * If no element is found then null is returned.
 */
export function ancestorOrSelf<T extends Element>(element: Element, selector: string): T | null {
    let node = element;

    while (node.parentElement != null) {
        if (node.matches(selector)) {
            return node as T;
        }

        node = node.parentElement;
    }

    return null;
}

/*
 * Helper function for toggling hidden class
 */
export function toggleVisibility(el: Element | null, override: boolean | null = null): boolean|null {
    if (!el) {
        return null;
    }

    if (override != null) {
        return !el.classList.toggle('hidden', !override);
    }

    return !el.classList.toggle('hidden');
}
